<template>
  <div class="fair-survey-step">
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <div class="review-step__rows">
      <div
        v-for="param in parameters"
        :key="param.label"
        class="review-step__row"
      >
        <span class="review-step__label">{{ param.label }}</span>

        <template v-if="editingParam === param.label">
          <input
            v-model="editValue"
            v-focustrap
            type="text"
            class="review-step__input"
            @keydown.enter.prevent="confirmEdit"
            @keydown.esc.prevent="editingParam = null"
          >
          <button
            type="button"
            class="review-step__action"
            aria-label="Сохранить"
            @click="confirmEdit"
          >
            <FairIcon
              name="check"
              :size="16"
            />
          </button>
        </template>

        <template v-else>
          <button
            type="button"
            class="review-step__value"
            @click="startEdit(param)"
          >
            <span>{{ param.value }}</span>
            <FairIcon
              name="edit"
              :size="14"
            />
          </button>
        </template>
      </div>
    </div>

    <p class="fair-survey-step__note">
      Значение можно поправить прямо здесь — нажмите на него.
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useSurveyModal } from '@/features/survey/store'
import { FairIcon } from '@/ui/primitives/FairIcon'
import type { SurveyStep, MeasurementType } from '@/features/survey/types/survey-config'

interface Props {
  step: SurveyStep & { type: 'review' }
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()

interface ParameterHint {
  hintName: string
  title: string
  video_src: string | null
  description: string
}

interface ParameterItem {
  label: string
  value: string
  dataKey: string
  hint?: ParameterHint
}

const editingParam = ref<string | null>(null)
const editValue = ref('')

const formatMeasurement = (value: number | null, unit = 'см'): string => {
  if (value === null || Number.isNaN(value)) {
    return '—'
  }
  return `${value} ${unit}`
}

const getMeasurementLabel = (type: MeasurementType): string => {
  const labels: Record<MeasurementType, string> = {
    height: 'Рост:',
    weight: 'Вес:',
    chest: 'Обхват груди:',
    underChest: 'Обхват под грудью:',
    waist: 'Обхват талии:',
    footLength: 'Длина стопы:',
    shoulderWidth: 'Ширина плеч:',
    inseamLength: 'Длина от талии до пола:',
    shoulderToWaist: 'Длина от плеч до талии:',
    armLength: 'Длина руки:',
    armCircumference: 'Обхват руки:',
    shoeSize: 'Размер обуви:'
  }
  return labels[type] || ''
}

const getMeasurementUnit = (type: MeasurementType): string => {
  if (type === 'weight') return 'кг'
  if (type === 'shoeSize') return 'размер'
  return 'см'
}

// Мемоизируем steps для оптимизации
const measurementSteps = computed(() => {
  return surveyStore.steps.filter(step => step.type === 'measurement' || step.type === 'shoe_size')
})

const parameters = computed<ParameterItem[]>(() => {
  const data = surveyStore.surveyModal.surveyData
  const params: ParameterItem[] = []

  // Add gender
  if (data.gender !== null && data.gender !== undefined) {
    params.push({
      label: 'Пол:',
      value: data.gender === 'male' ? 'Мужской' : data.gender === 'female' ? 'Женский' : '—',
      dataKey: 'gender',
      hint: {
        hintName: 'Как правильно определить пол',
        title: 'Пол',
        video_src: null,
        description: 'Описание для определения пола'
      }
    })
  }

  // Add measurements from steps - используем уже отфильтрованный список
  measurementSteps.value.forEach((step) => {
    const dataKey = step.id
    const value = data[dataKey]
    if (value !== null && value !== undefined) {
      const unit = step.unit || 'см'
      const hint = step.hint
      // Используем measurementType для label, если оно доступно, иначе используем dataKey
      const measurementType = step.measurementType || (dataKey as MeasurementType)

      params.push({
        label: getMeasurementLabel(measurementType),
        value: formatMeasurement(value, unit),
        dataKey: dataKey,
        hint: hint
          ? {
              hintName: hint.title,
              title: hint.title,
              video_src: hint.video_src,
              description: hint.description
            }
          : undefined
      })
    }
  })

  return params
})

function startEdit(param: ParameterItem) {
  editingParam.value = param.label
  if (param.dataKey === 'gender') {
    editValue.value = param.value
  } else {
    editValue.value = param.value.split(' ')[0] // get number
  }
}

function confirmEdit() {
  if (!editingParam.value) return
  const data = surveyStore.surveyModal.surveyData
  const param = parameters.value.find((p) => p.label === editingParam.value)
  if (!param) return

  if (param.dataKey === 'gender') {
    data.gender = editValue.value === 'Мужской' ? 'male' : 'female'
  } else {
    const numericValue = Number.parseFloat(editValue.value)
    if (!Number.isNaN(numericValue)) {
      data[param.dataKey] = numericValue
    }
  }
  editingParam.value = null
}
</script>

<style scoped>
.review-step__rows {
  display: flex;
  flex-direction: column;
}

.review-step__row {
  display: flex;
  align-items: center;
  gap: var(--fair-rd-space-12);
  min-height: 52px;
  padding-block: var(--fair-rd-space-8);
  border-bottom: 1px solid var(--fair-rd-line-hairline);
}

.review-step__row:last-child {
  border-bottom: 0;
}

.review-step__label {
  flex: 1 1 auto;
  min-width: 0;
  font-family: var(--fair-font-body, Inter, sans-serif);
  font-size: 15px;
  line-height: 1.2;
  color: var(--fair-rd-text-primary);
}

.review-step__value {
  display: inline-flex;
  align-items: center;
  gap: var(--fair-rd-space-8);
  margin: 0;
  padding: var(--fair-rd-space-4) 0;
  border: 0;
  background: transparent;
  font-family: var(--fair-font-body, Inter, sans-serif);
  font-size: 15px;
  line-height: 1.2;
  color: var(--fair-rd-text-muted);
  cursor: pointer;
  transition: color 0.15s ease;
}

.review-step__value:hover {
  color: var(--fair-rd-text-primary);
}

.review-step__input {
  box-sizing: border-box;
  width: 120px;
  padding: var(--fair-rd-space-8) var(--fair-rd-space-12);
  border: 1px solid var(--fair-rd-border-control);
  border-radius: var(--fair-rd-radius-control);
  background: var(--fair-rd-bg-surface);
  font-family: var(--fair-font-body, Inter, sans-serif);
  font-size: 15px;
  color: var(--fair-rd-text-primary);
}

.review-step__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: var(--fair-rd-space-8);
  border: 0;
  border-radius: var(--fair-rd-radius-control);
  background: var(--fair-rd-fill-chip);
  color: var(--fair-rd-text-primary);
  cursor: pointer;
}

@media (prefers-reduced-motion: reduce) {
  .review-step__value {
    transition: none;
  }
}
</style>
